import React, { useMemo, useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/react';
import { ResponsiveModal, Typography } from '../../../components';
import CustomHeroButton from '../../../components/button/index';
import CustomHeroInput from '../../../components/CustomHeroInput';
import AlertBlock from '../../../components/alert-block';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useTranslations } from '../../../contexts/translation.context';

interface AddWalletModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (walletData: { address: string; network: string }) => void;
}

const AddWalletModal: React.FC<AddWalletModalProps> = ({ isOpen, onClose, onSave }) => {
  const { t } = useTranslations();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        address: Yup.string()
          .required(t('wallet.addWallet.errors.addressRequired'))
          .min(10, t('wallet.addWallet.errors.addressMin')),
        network: Yup.string().required(t('wallet.addWallet.errors.networkRequired')),
      }),
    [t]
  );

  const handleSubmit = async (values: { address: string; network: string }) => {
    setIsSubmitting(true);
    try {
      if (onSave) {
        await onSave(values);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save wallet address:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      scrollBehavior="inside"
      classNames={{
        base: 'max-h-[90vh]',
        wrapper: 'z-[9999]',
        backdrop: 'z-[9998]',
      }}
    >
      <ModalContent className="md:w-[37.5rem] md:max-w-[37.5rem]">
        <ModalHeader className="flex flex-col gap-1 px-4 md:px-6 py-4 ">
          <Typography variant="h6" className="font-semibold text-xl leading-6">
            {t('wallet.addWallet.title')}
          </Typography>
        </ModalHeader>

        <Formik
          initialValues={{ address: '', network: 'TRC20' }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue, submitForm }) => (
            <Form>
              <ModalBody className="px-4 md:px-6 py-5 space-y-6">
                <div className="space-y-4">
                  <AlertBlock
                    data={[
                      {
                        severity: 'info',
                        title: t('wallet.addWallet.alert.title'),
                        descriptions: [
                          t('wallet.addWallet.alert.description1'),
                          t('wallet.addWallet.alert.description2'),
                        ],
                      },
                    ]}
                  />
                  <div className="flex items-center w-full gap-2">
                    <CustomHeroInput
                      label={t('wallet.addWallet.addressLabel')}
                      type="text"
                      name="address"
                      value={values.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.address}
                      touched={touched.address}
                      placeholder={t('wallet.addWallet.addressPlaceholder')}
                      className="w-full"
                    />
                    <CustomHeroButton
                      title={t('wallet.addWallet.addButton')}
                      onPress={onClose}
                      variant="secondary"
                      className="mt-6 !font-regular"
                    />
                  </div>
                </div>
              </ModalBody>

              <ModalFooter className="px-4 md:px-6 py-4 border-t border-gray-200 gap-3">
                <CustomHeroButton
                  title={t('wallet.addWallet.backButton')}
                  onPress={onClose}
                  variant="tertiary"
                  className="px-4 !bg-auth-strength-weak/20 !font-regular !text-auth-strength-weak"
                />
                <CustomHeroButton
                  title={t('wallet.addWallet.saveButton')}
                  onPress={() => submitForm()}
                  variant="primary"
                  className="px-4"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                />
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </ResponsiveModal>
  );
};

export default AddWalletModal;
